import { useState, useEffect } from "react";
import {
    Drawer,
    Box,
    IconButton,
    Typography,
    Card,
    CardMedia,
    CardContent,
    TextField,
    Grid, useMediaQuery, CircularProgress
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { setMessage, } from "../messagesSlice";
import { uploadFileThunk } from "../../../components/general/generalThunk";
import SnackbarMessage from "../../../components/snackBar/snackBar";

export default function ConvoMediaFiles({ open, handleClose, selectedFiles, removeFile, handleMessageSubmit }) {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery("(max-width:600px)");
    const { typeMessage } = useSelector((state) => state.messages);
    const { status, error } = useSelector((state) => state.general);
  
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleMessageChange = (e) => {
        dispatch(setMessage(e.target.value));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFiles?.length) {
            setSnackbar({
                open: true,
                message: "Please select a file",
                severity: "error",
            });
            return;
        }

        const firstFile = selectedFiles[0].file || selectedFiles[0];

        let messageType = "";
        if (firstFile.type.startsWith("image/")) messageType = "image";
        else if (firstFile.type.startsWith("video/")) messageType = "video";
        else if (firstFile.type === "application/pdf") messageType = "document";
        else {
            setSnackbar({
                open: true,
                message: "Unsupported file type",
                severity: "error",
            });
            return;
        }

        // Prepare files for upload
        const filesPayload = selectedFiles.map((item) => {
            const file = item.file || item;
            const previewUrl = item.base64Data || URL.createObjectURL(file);
            return {
                filename: file.name,
                img: previewUrl,
            };
        });

        try {
            // Dispatch upload thunk
            const uploadResult = await dispatch(
                uploadFileThunk({
                    credentials: { file: filesPayload, category: "userImages" },
                })
            );

            if (
                uploadResult.meta.requestStatus !== "fulfilled" ||
                !uploadResult.payload?.data?.length
            ) {
                setSnackbar({
                    open: true,
                    message: "File upload failed",
                    severity: "error",
                });
                return;
            }

            const uploadedPath = uploadResult.payload.data;

            setSnackbar({
                open: true,
                message: "File uploaded successfully",
                severity: "success",
            });

            await handleMessageSubmit(e, uploadedPath);
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Something went wrong",
                severity: "error",
            });
        }
    };

    // 🔹 Snackbar for errors
    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, maxWidth: "100vw", p: { xs: 1.5, sm: 2 } } }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6">Uploaded Files</Typography>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Files */}
            <Box sx={{
                flexGrow: 1,
                overflowY: "auto",
                maxHeight: "80vh"
            }}>

                {status === "loading" ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                        <CircularProgress color="primary" size={48} />
                    </Box>) : (
                    <Grid container spacing={2}>
                        {selectedFiles.map((item, index) => {
                            const file = item.file || item; // support both File object or object with file
                            const previewUrl = item.base64Data || URL.createObjectURL(file);

                            const isImage = file.type.startsWith("image/");
                            const isVideo = file.type.startsWith("video/");
                            const isPDF = file.type === "application/pdf";

                            return (
                                <Grid item xs={12} key={index}>
                                    <Card
                                        sx={{
                                            width: 150,
                                            height: "auto",
                                            position: "relative",
                                            border: "1px solid #ddd",
                                            boxShadow: 1,
                                        }}
                                    >
                                        {/* Close Button */}
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(index)}
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                backgroundColor: "var(--btn-1)",
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>

                                        {/* Preview */}
                                        {isImage ? (
                                            <CardMedia
                                                component="img"
                                                src={previewUrl}
                                                alt={file.name}
                                                sx={{ height: 120, objectFit: "cover" }}
                                            />
                                        ) : isVideo ? (
                                            <CardMedia
                                                component="video"
                                                src={previewUrl}
                                                controls
                                                sx={{ height: 120, objectFit: "cover" }}
                                            />
                                        ) : isPDF ? (
                                            <Box
                                                sx={{
                                                    height: 120,
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    bgcolor: "#f5f5f5",
                                                    fontSize: 14,
                                                    textAlign: "center",
                                                    p: 1,
                                                }}
                                            >
                                                <iframe src={previewUrl} width="100%" height="100%" title={file.name}></iframe>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 120,
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    bgcolor: "#f5f5f5",
                                                    fontSize: 14,
                                                    textAlign: "center",
                                                    p: 1,
                                                }}
                                            >
                                                {file.name.split(".").pop().toUpperCase()}
                                            </Box>
                                        )}

                                        {/* File Name */}
                                        <CardContent
                                            sx={{
                                                p: 1,
                                                textAlign: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <Typography variant="body2" noWrap title={file.name}>
                                                {file.name}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

            </Box>

            <Box sx={{ display: "flex", p: 1, gap: 1 }}>
                <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={2}
                    value={typeMessage}
                    onChange={handleMessageChange}
                    size="small"
                    placeholder="Type a message"
                    variant="outlined"
                    onKeyDown={handleKeyDown}
                    sx={{
                        bgcolor: "background.default",
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            paddingLeft: 2,
                            paddingRight: 2,
                        },
                    }}
                />

                <IconButton
                    onClick={handleSubmit}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 400,
                        color: "var(--primary)",
                        borderColor: "var(--primary)",
                        borderWidth: "2px",
                        display: "flex",
                        alignItems: "center",
                        padding: "0px 12px",
                        "&:hover": {
                            borderColor: "var(--primary)",
                            backgroundColor: "var(--background)"
                        }
                    }}
                >
                    <SendIcon />
                </IconButton>
            </Box>
            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Drawer>
    );
}